// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { serverTimestamp, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { auth } from '@/services/firebase';
import { FirestoreService } from '@/services/FirestoreService';
import type { EarningsDocument } from '@/types/firestore';
import type { DateRange } from '@/services/distribution/proprietary-ingestion/types/common';
import type { DistributorEarnings, DistributorId } from './types/distributor';
import { logger } from '@/utils/logger';

/**
 * Earnings Service
 * Manages tracking of digital sales revenue and stream counts from DSPs.
 */
export class EarningsService extends FirestoreService<EarningsDocument> {
    constructor() {
        super('earnings');
    }

    /**
     * Get earnings for a specific release from a specific distributor
     */
    async getEarnings(distributorId: string, releaseId: string, period?: DateRange): Promise<DistributorEarnings | null> {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return null;

            const baseConstraints = [
                this.where('userId', '==', userId),
                this.where('distributorId', '==', distributorId),
                this.where('releaseId', '==', releaseId)
            ];

            const constraints = period ? [
                ...baseConstraints,
                this.where('period.startDate', '==', period.startDate),
                this.where('period.endDate', '==', period.endDate)
            ] : [
                ...baseConstraints,
                this.orderBy('period.endDate', 'desc'),
                limit(1)
            ];

            const results = await this.list(constraints);
            if (results.length === 0) return null;

            const doc = results[0]!;
            return this.mapToEarnings(doc);
        } catch (error: unknown) {
            logger.error('[EarningsService] Failed to fetch earnings:', error);
            return null;
        }
    }

    /**
     * Get all earnings for a distributor (optionally filtered by period)
     */
    async getAllEarnings(distributorId: string, period?: DateRange): Promise<DistributorEarnings[]> {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return [];

            const constraints = [
                this.where('userId', '==', userId),
                this.where('distributorId', '==', distributorId)
            ];

            if (period) {
                constraints.push(
                    this.where('period.startDate', '>=', period.startDate),
                    this.where('period.endDate', '<=', period.endDate)
                );
            }

            const results = await this.list(constraints);
            return results.map(this.mapToEarnings);
        } catch (error: unknown) {
            logger.error('[EarningsService] Failed to fetch all earnings:', error);
            return [];
        }
    }

    /**
     * Deterministic earnings doc ID — shared by recordEarnings() and any caller
     * (e.g. EarningsUploadService's atomic batch write) that needs to write to
     * the same collection/ID scheme without going through a single-doc `set`.
     */
    static buildEarningsId(distributorId: string, releaseId: string, period: { startDate: string; endDate: string }): string {
        return `${distributorId}_${releaseId}_${period.startDate}_${period.endDate}`;
    }

    /**
     * Record new earnings data (Ingest)
     */
    async recordEarnings(data: Omit<EarningsDocument, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<string> {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('User not authenticated');

            const earningsId = EarningsService.buildEarningsId(data.distributorId, data.releaseId, data.period);

            const record: EarningsDocument = {
                ...data,
                userId,
                id: earningsId,
                createdAt: serverTimestamp() as unknown as Timestamp,
                updatedAt: serverTimestamp() as unknown as Timestamp
            };

            await this.set(earningsId, record);
            return earningsId;
        } catch (error: unknown) {
            logger.error('[EarningsService] Failed to record earnings:', error);
            throw error;
        }
    }

    private mapToEarnings(doc: EarningsDocument): DistributorEarnings {
        return {
            ...doc,
            distributorId: doc.distributorId as DistributorId,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            lastUpdated: (doc.updatedAt && typeof (doc.updatedAt as { toDate?: () => Date }).toDate === 'function') ? (doc.updatedAt as { toDate: () => Date }).toDate().toISOString() : (doc.updatedAt ? new Date(doc.updatedAt as any).toISOString() : new Date().toISOString())
        };
    }
}

export const earningsService = new EarningsService();
